import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import API_BASE_URL from '../../config';
import loadingGif from '../../assets/loading.gif';
import {
    Box,
    Card,
    Typography,
    IconButton,
    Button,
    Divider,
    Grid,
    TextField,
    LinearProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Switch,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from '@mui/material';
import KeyboardReturnRoundedIcon from '@mui/icons-material/KeyboardReturnRounded';
import './JobFlowSettings.css';

const JobFlowSettings = () => {
    const { orgId } = useParams();
    const [jobFlows, setJobFlows] = useState([]);
    const [organization, setOrganization] = useState(null);
    const [jobFlowData, setJobFlowData] = useState({ maxJobFlow: 0, jobFlowCount: 0 });
    const [loading, setLoading] = useState(true);
    const [deleteJobFlowDialogOpen, setDeleteJobFlowDialogOpen] = useState(false);
    const [selectedDeleteJobFlow, setSelectedDeleteJobFlow] = useState(null);
    const [deleteJobFlowConfirmName, setDeleteJobFlowConfirmName] = useState('');

    const navigate = useNavigate();

    useEffect(() => {
        setLoading(true);
        const fetchOrganizationDetails = async () => {
            const token = localStorage.getItem('token');

            if (!token) {
                navigate('/unauthorized');
                return;
            }

            try {
                const orgResponse = await axios.get(`${API_BASE_URL}/org/organization-settings/organization-name`, {
                    headers: { Authorization: `Bearer ${token}` },
                    params: { orgId },
                });

                if (orgResponse.status === 403) {
                    navigate('/unauthorized');
                    return;
                }
                setOrganization(orgResponse.data);

                const jobFlowResponse = await axios.get(`${API_BASE_URL}/org/organization-settings/job-flow-max-count`, {
                    headers: { Authorization: `Bearer ${token}` },
                    params: { orgId },
                });

                if (jobFlowResponse.status === 403) {
                    navigate('/unauthorized');
                    return;
                }
                setJobFlowData(jobFlowResponse.data);

                const response = await axios.get(`${API_BASE_URL}/org/organization-settings/job-flow/all`, {
                    headers: { Authorization: `Bearer ${token}` },
                    params: { orgId },
                });

                if (response.status === 404) {
                    Swal.fire({
                        title: 'Error',
                        text: 'No job flows found for this organization.',
                        icon: 'error',
                        confirmButtonText: 'OK',
                    });
                    setJobFlows([]);
                    return;
                }

                if (response.status === 403) {
                    navigate('/unauthorized');
                    return;
                }

                setJobFlows(response.data);

            } catch (error) {
                console.error('Error fetching data:', error);
                if (error.response && error.response.status === 404) {
                    const result = await Swal.fire({
                        title: 'Notice',
                        text: 'No job flows found for this organization. Please create a new job flow to continue.',
                        icon: 'info',
                        confirmButtonText: 'Go to Settings',
                    });

                    if (result.isConfirmed) {
                        navigate('/organization/settings');
                    }
                } else {
                    navigate('/unauthorized');
                    return;
                }
            } finally {
                setLoading(false);
            }
        };

        fetchOrganizationDetails();
    }, [orgId, navigate]);

    const handleEdit = (orgId, jobFlowId) => {
        navigate(`/organization/settings/job-flow-settings/${orgId}/edit/${jobFlowId}`);
    };

    const handleDeleteJobFlow = (jobFlow) => {
        setSelectedDeleteJobFlow(jobFlow); // Set the job flow to be deleted
        setDeleteJobFlowDialogOpen(true);   // Open the dialog
        setDeleteJobFlowConfirmName(""); // Reset input field
    };

    const handleDeleteJobFlowCancel = () => {
        // Close the delete confirmation dialog
        setDeleteJobFlowDialogOpen(false);
        setDeleteJobFlowConfirmName("");
        setSelectedDeleteJobFlow(null);
    };

    const handleDeleteJobFlowConfirm = async () => {
        // Perform the delete operation for the selected job flow
        if (selectedDeleteJobFlow) {
            try {
                handleDelete(selectedDeleteJobFlow.id); // Replace with your actual delete function
                setDeleteJobFlowDialogOpen(false);
                setDeleteJobFlowConfirmName("");
                setSelectedDeleteJobFlow(null);
                // Optionally refresh job flows list here
            } catch (error) {
                // Handle error (show notification, etc.)
            }
        }
    };

    const handleDelete = (jobFlowId) => {
        axios.post(`${API_BASE_URL}/org/organization-settings/job-flow/delete`, {
            jobFlowId: jobFlowId,
            orgId: orgId
        }, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        })
        .then((response) => {
            Swal.fire('Deleted!', 'Job flow has been deleted.', 'success');
            window.location.reload();
        })
        .catch((error) => {
            console.error('Error deleting job flow:', error);
            Swal.fire('Error!', 'Failed to delete the job flow.', 'error');
        });

    };

    const handleDefaultChange = async (jobFlowId) => {
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/unauthorized');
            return;
        }

        try {
            const response = await axios.post(`${API_BASE_URL}/org/organization-settings/job-flow/default`, 
                { orgId, jobFlowId },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.status === 201) {
                setJobFlows(prevJobFlows => prevJobFlows.map(jobFlow => ({
                    ...jobFlow,
                    default_job_flow: jobFlow.id === jobFlowId ? 1 : 0
                })));
                Swal.fire({
                    title: 'Success',
                    text: 'Default job flow updated successfully.',
                    icon: 'success',
                    confirmButtonText: 'OK'
                });
            }
        } catch (error) {
            console.error('Error updating default job flow:', error);
            Swal.fire({
                title: 'Error',
                text: 'There was an error updating the default job flow.',
                icon: 'error',
                confirmButtonText: 'OK'
            });
        }
    };

    if (loading) {
        return (
        <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: 'calc(100vh - 64px)',
            backgroundColor: '#fff',
        }}>
            <img src={loadingGif} alt="Loading..." style={{ width: '30%', height: '30%', objectFit: 'contain' }} />
        </div>
        );
    }

    const progressPercentage = Math.round(Math.min((jobFlowData.jobFlowCount / jobFlowData.maxJobFlow) * 100, 100));

    return (
        <>
            <Box sx={{ width: '100%', margin: 'auto', mt: 0 }}>
                <Card sx={{ width: '100%', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', p: 2 }}>
                    <IconButton onClick={() => navigate(-1)} sx={{ mr: 2 }}>
                    <KeyboardReturnRoundedIcon />
                    </IconButton>
                    <Typography variant="h5" sx={{ flexGrow: 1 }}>
                    {organization ? `Edit Job Flow for ${organization.orgName}` : 'Edit Job Flow'}
                    </Typography>
                </Box>
                <Divider />
                <Box sx={{ p: 3 }}>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                    Current Plan Usage: {jobFlowData.jobFlowCount} / {jobFlowData.maxJobFlow} Job Flow Version(s)
                    </Typography>
                    <Box sx={{ mb: 3 }}>
                    <LinearProgress variant="determinate" value={progressPercentage} sx={{ height: 10, borderRadius: 5 }} />
                    <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>{progressPercentage}%</Typography>
                    </Box>
                    <Box>
                    {jobFlows.length > 0 ? (
                        <Table>
                        <TableHead>
                            <TableRow>
                            <TableCell>Job Flow Name</TableCell>
                            <TableCell>Default Job Flow</TableCell>
                            <TableCell>Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {jobFlows.map(jobFlow => (
                            <TableRow key={jobFlow.id}>
                                <TableCell>{jobFlow.flow_name}</TableCell>
                                <TableCell>
                                <Switch
                                    checked={jobFlow.default_job_flow === 1}
                                    onChange={() => handleDefaultChange(jobFlow.id)}
                                    color="primary"
                                />
                                </TableCell>
                                <TableCell>
                                <Button variant="outlined" size="small" sx={{ mr: 1 }} onClick={() => handleEdit(orgId, jobFlow.id)}>
                                    Edit
                                </Button>
                                <Button variant="outlined" color="error" size="small" onClick={() => handleDeleteJobFlow(jobFlow)}>
                                    Delete
                                </Button>
                                </TableCell>
                            </TableRow>
                            ))}
                        </TableBody>
                        </Table>
                    ) : (
                        <Typography variant="body2">No job flows found.</Typography>
                    )}
                    </Box>
                </Box>
                </Card>
            </Box>
            <Dialog
                open={deleteJobFlowDialogOpen}
                onClose={handleDeleteJobFlowCancel}
                maxWidth="sm"
                fullWidth
                >
                <DialogTitle style={{ color: 'black' }}>Delete Job Flow</DialogTitle>
                <DialogContent>
                    <div style={{ marginBottom: 16 }}>
                    Please type <b>{selectedDeleteJobFlow?.flow_name}</b> to confirm deletion.
                    </div>
                    <TextField
                    label="Type the job flow name to confirm"
                    variant="outlined"
                    multiline
                    rows={1}
                    fullWidth
                    value={deleteJobFlowConfirmName}
                    onChange={e => setDeleteJobFlowConfirmName(e.target.value)}
                    autoFocus
                    margin="normal"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteJobFlowCancel} style={{ padding: '6px 12px' }}>
                    Cancel
                    </Button>
                    <Button
                    onClick={handleDeleteJobFlowConfirm}
                    variant="contained"
                    color="error"
                    style={{ padding: '6px 12px' }}
                    disabled={
                        !selectedDeleteJobFlow ||
                        !deleteJobFlowConfirmName ||
                        (selectedDeleteJobFlow?.flow_name !== deleteJobFlowConfirmName)
                    }
                    >
                    Delete
                    </Button>
                </DialogActions>
                </Dialog>
        </>
        );
};

export default JobFlowSettings;

